import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type MaybeRef, unref } from 'vue'

import { api } from '@/api/lunchClient'
import { User } from '@/models/User'
import type {
  AttendanceDto,
  CatererDto,
  DietPreferenceDto,
  DietPreferencePayload,
  MenuItemDto,
  OfficeDto,
  OrderDto,
  OrderRoundDto,
  ProductListDto,
  Resource,
  ResourceList,
  WeekBlockDto,
  WeekdayPreferenceDto,
  WeekdayPreferencePayload,
} from '@/api/lunchTypes'

const baseKey = ['lunchtool'] as const

export const lunchKeys = {
  all: baseKey,
  caterers: [...baseKey, 'caterers'] as const,
  caterer: (id: string) => [...baseKey, 'caterers', id] as const,
  menuItems: [...baseKey, 'menu-items'] as const,
  productLists: [...baseKey, 'product-lists'] as const,
  productList: (id: string) => [...baseKey, 'product-lists', id] as const,
  rounds: [...baseKey, 'order-rounds'] as const,
  round: (id: string) => [...baseKey, 'order-rounds', id] as const,
  myWeeks: [...baseKey, 'me', 'order-rounds'] as const,
  orders: [...baseKey, 'orders'] as const,
  dayPreferences: (range?: { from?: string; to?: string; all?: true }) =>
    [...baseKey, 'day-preferences', range?.from ?? null, range?.to ?? null, range?.all ?? null] as const,
  attendance: (date: string, officeId: string) => [...baseKey, 'attendance', date, officeId] as const,
  user: (userId: string) => [...baseKey, 'users', userId] as const,
  dietPreferences: (userId: string) => [...baseKey, 'users', userId, 'diet-preferences'] as const,
  weekdayPreferences: (userId: string) => [...baseKey, 'users', userId, 'weekday-preferences'] as const,
}

export function useCaterers() {
  return useQuery({
    queryKey: lunchKeys.caterers,
    queryFn: () => api.get<ResourceList<CatererDto>>('/v0/lunchtool/caterers').then((response) => response.data),
  })
}

export function useCaterer(id: MaybeRef<string>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.caterer(unref(id))),
    queryFn: () => api.get<Resource<CatererDto>>(`/v0/lunchtool/caterers/${unref(id)}`).then((response) => response.data),
    enabled: computed(() => Boolean(unref(id))),
  })
}

export function useMenuItems() {
  return useQuery({
    queryKey: lunchKeys.menuItems,
    queryFn: () => api.get<ResourceList<MenuItemDto>>('/v0/lunchtool/menu-items').then((response) => response.data),
  })
}

export function useProductLists() {
  return useQuery({
    queryKey: lunchKeys.productLists,
    queryFn: () => api.get<ResourceList<ProductListDto>>('/v0/lunchtool/product-lists').then((response) => response.data),
  })
}

export function useProductList(id: MaybeRef<string>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.productList(unref(id))),
    queryFn: () => api.get<Resource<ProductListDto>>(`/v0/lunchtool/product-lists/${unref(id)}`).then((response) => response.data),
    enabled: computed(() => Boolean(unref(id))),
  })
}

export function useOrderRounds() {
  return useQuery({
    queryKey: lunchKeys.rounds,
    queryFn: () => api.get<ResourceList<OrderRoundDto>>('/v0/lunchtool/order-rounds').then((response) => response.data),
  })
}

export function useOrderRound(id: MaybeRef<string>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.round(unref(id))),
    queryFn: () =>
      api
        .get<Resource<OrderRoundDto>>(`/v0/lunchtool/order-rounds/${unref(id)}?with[]=caterer&with[]=orders&with[]=office`)
        .then((response) => response.data),
    enabled: computed(() => Boolean(unref(id))),
  })
}

export function useMyOrderForRound(roundId: MaybeRef<string>) {
  return useQuery({
    queryKey: computed(() => [...lunchKeys.round(unref(roundId)), 'me'] as const),
    queryFn: async (): Promise<OrderDto | null> => {
      try {
        const response = await api.get<Resource<OrderDto> | undefined>(`/v0/lunchtool/order-rounds/${unref(roundId)}/me/order`)
        return response?.data ?? null
      } catch {
        return null
      }
    },
    enabled: computed(() => Boolean(unref(roundId))),
  })
}

export function useMyWeeks() {
  return useQuery({
    queryKey: lunchKeys.myWeeks,
    queryFn: () => api.get<ResourceList<WeekBlockDto>>('/v0/lunchtool/me/order-rounds').then((response) => response.data),
  })
}

export function usePlaceOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      roundId,
      items,
      userId,
      note,
    }: {
      roundId: string
      items: { menu_item_id: string; quantity: number; note?: string | null }[]
      userId?: string
      note?: string | null
    }) =>
      api
        .post<Resource<OrderDto>>(`/v0/lunchtool/order-rounds/${roundId}/orders`, {
          items,
          note,
          user_id: userId,
        })
        .then((response) => response.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: lunchKeys.orders })
      queryClient.invalidateQueries({ queryKey: lunchKeys.rounds })
      queryClient.invalidateQueries({ queryKey: lunchKeys.myWeeks })
      queryClient.invalidateQueries({ queryKey: lunchKeys.round(variables.roundId) })
    },
  })
}

export function useUpsertCaterer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (caterer: Partial<CatererDto> & { id?: string; logo?: File | null }): Promise<CatererDto> => {
      const { logo, ...attributes } = caterer

      const saved = attributes.id
        ? await api.put<Resource<CatererDto>>(`/v0/lunchtool/caterers/${attributes.id}`, attributes).then((response) => response.data)
        : await api.post<Resource<CatererDto>>('/v0/lunchtool/caterers', attributes).then((response) => response.data)

      if (!logo) return saved

      return api.postForm<Resource<CatererDto>>(`/v0/lunchtool/caterers/${saved.id}/logo`, { logo }).then((response) => response.data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: lunchKeys.caterers }),
  })
}

export function useUpsertMenuItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (item: Partial<MenuItemDto> & { id?: string }) => {
      if (item.id) {
        return api.put<Resource<MenuItemDto>>(`/v0/lunchtool/menu-items/${item.id}`, item).then((response) => response.data)
      }
      return api.post<Resource<MenuItemDto>>('/v0/lunchtool/menu-items', item).then((response) => response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lunchKeys.menuItems })
      queryClient.invalidateQueries({ queryKey: lunchKeys.caterers })
    },
  })
}

export type { OfficeDto } from '@/api/lunchTypes'

// 'weekday_default' rows are projected from the user's standard week and may be
// regenerated by a later weekday-preferences PUT; 'manual' means the user owns that
// date and materialisation leaves it alone. Optional because a row written before the
// column existed deserialises without it -- treat a missing origin as not yet owned.
export type DayPreferenceOrigin = 'manual' | 'weekday_default'

export type DayPreferenceDto = {
  id: string
  user_id: string
  date: string
  attending: boolean
  office_id: string | null
  office?: OfficeDto | null
  origin?: DayPreferenceOrigin
  created_at: string | null
  updated_at: string | null
}

// OfficeController::index returns a Laravel paginator envelope, not a bare ResourceList --
// `per_page` defaults to 25, so reading only `data` silently truncates the office list.
interface OfficesPaginatedResponse {
  data: OfficeDto[]
  total?: number
}

// Comfortably above any realistic office count. Paired with the total-vs-received check
// below so a future office count that somehow exceeds it fails loudly instead of quietly
// truncating the list again.
const officesRequestLimit = 500

export function useOffices() {
  const query = useQuery({
    queryKey: [...lunchKeys.all, 'offices'] as const,
    queryFn: async (): Promise<OfficeDto[]> => {
      const response = await api.get<OfficesPaginatedResponse>(`/v0/lunchtool/offices?limit=${officesRequestLimit}`)

      if (typeof response.total === 'number' && response.total > response.data.length) {
        throw new Error(`Offices response was truncated: received ${response.data.length} of ${response.total} offices.`)
      }

      return response.data
    },
  })

  const activeOffices = computed<OfficeDto[]>(() =>
    (query.data.value ?? []).filter((office) => office.is_active).sort((officeA, officeB) => officeA.sort_order - officeB.sort_order),
  )

  return Object.assign(query, { activeOffices })
}

interface AttendancePaginatedResponse {
  data: AttendanceDto[]
  total?: number
}

// The attendance endpoint caps `limit` at 100; one office on one day stays far below that.
const attendanceRequestLimit = 100

/** Who attends the given office on each of the given dates, one query per date. */
export function useOfficeAttendance(officeId: MaybeRef<string>, dates: MaybeRef<string[]>) {
  const queries = useQueries({
    queries: computed(() =>
      unref(dates).map((date) => ({
        queryKey: lunchKeys.attendance(date, unref(officeId)),
        queryFn: async (): Promise<AttendanceDto[]> => {
          const parameters = new URLSearchParams({
            'filters[date]': date,
            'filters[office_id]': unref(officeId),
            limit: String(attendanceRequestLimit),
          })
          const response = await api.get<AttendancePaginatedResponse>(`/v0/lunchtool/attendance?${parameters.toString()}`)

          if (typeof response.total === 'number' && response.total > response.data.length) {
            throw new Error(`Attendance response was truncated: received ${response.data.length} of ${response.total} attendees.`)
          }

          return response.data
        },
      })),
    ),
  })

  const attendanceByDate = computed((): Map<string, AttendanceDto[]> => {
    const result = new Map<string, AttendanceDto[]>()
    for (const [index, date] of unref(dates).entries()) result.set(date, queries.value[index]?.data ?? [])
    return result
  })
  const isPending = computed((): boolean => queries.value.some((query) => query.isPending))
  const isError = computed((): boolean => queries.value.some((query) => query.isError))

  return { attendanceByDate, isPending, isError }
}

/** Everyone attending any office on one date, following the paginator to the last page. */
export function useDayAttendance(date: MaybeRef<string>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.attendance(unref(date), 'all')),
    queryFn: async (): Promise<AttendanceDto[]> => {
      const attendees: AttendanceDto[] = []
      for (let page = 1; ; page++) {
        const parameters = new URLSearchParams({ 'filters[date]': unref(date), limit: String(attendanceRequestLimit), page: String(page) })
        const response = await api.get<AttendancePaginatedResponse>(`/v0/lunchtool/attendance?${parameters.toString()}`)
        attendees.push(...response.data)
        if (response.data.length < attendanceRequestLimit || attendees.length >= (response.total ?? 0)) return attendees
      }
    },
  })
}

export function useDayPreferences(range?: MaybeRef<{ from?: string; to?: string; all?: true } | undefined>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.dayPreferences(unref(range))),
    queryFn: () => {
      const response = unref(range)
      const parameters = new URLSearchParams()
      if (response?.from) parameters.set('from', response.from)
      if (response?.to) parameters.set('to', response.to)
      if (response?.all) parameters.set('all', String(response.all))
      const suffix = parameters.toString() ? `?${parameters.toString()}` : ''
      return api.get<ResourceList<DayPreferenceDto>>(`/v0/lunchtool/day-preferences${suffix}`).then((response) => response.data)
    },
  })
}

export function useUpsertDayPreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { date: string; attending: boolean; office_id?: string | null }) =>
      api.post<Resource<DayPreferenceDto>>('/v0/lunchtool/day-preferences', payload).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...lunchKeys.all, 'day-preferences'] })
    },
  })
}

// Weekday preferences are the per-weekday defaults (attend? + location) the backend materialises into
// future day-preferences for every upcoming week, unless an individual day has been overridden. The
// endpoint is scoped to the user in the path, like diet-preferences -- not to the token.
/** One user with their roles, for the admin user page. */
export function useUser(userId: MaybeRef<string>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.user(unref(userId))),
    queryFn: (): Promise<User | null> => new User().include('roles').find(unref(userId)),
  })
}

export function useWeekdayPreferences(userId: MaybeRef<string | undefined>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.weekdayPreferences(unref(userId) ?? '')),
    queryFn: () => api.get<ResourceList<WeekdayPreferenceDto>>(`/v0/users/${unref(userId)}/weekday-preferences`).then((response) => response.data),
    enabled: computed(() => Boolean(unref(userId))),
  })
}

export function useUpsertWeekdayPreferences(userId: MaybeRef<string | undefined>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: WeekdayPreferencePayload) =>
      api.put<ResourceList<WeekdayPreferenceDto>>(`/v0/users/${unref(userId)}/weekday-preferences`, payload).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lunchKeys.weekdayPreferences(unref(userId) ?? '') })
      // Materialised day-preferences and the user's week overview may now reflect the new defaults.
      queryClient.invalidateQueries({ queryKey: [...lunchKeys.all, 'day-preferences'] })
      queryClient.invalidateQueries({ queryKey: lunchKeys.myWeeks })
    },
  })
}

// The diet-preferences endpoints return the bare object (NOT wrapped in a `data` key), so parse the body directly.
export function useDietPreferences(userId: MaybeRef<string | undefined>) {
  return useQuery({
    queryKey: computed(() => lunchKeys.dietPreferences(unref(userId) ?? '')),
    queryFn: () => api.get<DietPreferenceDto>(`/v0/users/${unref(userId)}/diet-preferences`),
    enabled: computed(() => Boolean(unref(userId))),
  })
}

export function useUpsertDietPreferences(userId: MaybeRef<string | undefined>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: DietPreferencePayload): Promise<DietPreferenceDto> =>
      api.put<DietPreferenceDto>(`/v0/users/${unref(userId)}/diet-preferences`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lunchKeys.dietPreferences(unref(userId) ?? '') })
    },
  })
}

export function useUpsertRound() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (round: Partial<OrderRoundDto> & { id?: string }) => {
      if (round.id) {
        return api.put<Resource<OrderRoundDto>>(`/v0/lunchtool/order-rounds/${round.id}`, round).then((response) => response.data)
      }
      return api.post<Resource<OrderRoundDto>>('/v0/lunchtool/order-rounds', round).then((response) => response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lunchKeys.rounds })
      queryClient.invalidateQueries({ queryKey: lunchKeys.myWeeks })
    },
  })
}
